import { useCallback, useEffect, useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import clsx from 'clsx'
import { observer } from 'mobx-react-lite'

import {
  GuestsLimits,
  type GuestsState,
  RoomsInfoMessage,
  calculateRoomsCount,
  useSelectGuests,
} from 'entities/kyrgyzstan'

import { RealtyCapacityResponse } from 'shared/api/kyrgyzstan/search'
import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types'
import { ButtonContinue } from 'shared/ui/bottom-continue'
import { Button } from 'shared/ui/button'
import { ButtonInputValue } from 'shared/ui/button-input-value'
import { Paper } from 'shared/ui/paper'

import s from './styles.module.scss'

export interface SelectGuestsFormProps {
  initialState?: GuestsState
  maxGuests?: number
  capacity?: RealtyCapacityResponse
  submitButtonText?: string
  showSubmitButton?: boolean
  isShowInfo?: boolean
  onGuestsChange?: (guests: GuestsState) => void
  onSubmit?: (guests: GuestsState) => void
}

export const SelectGuestsForm = observer(
  ({
    initialState,
    maxGuests,
    capacity,
    isShowInfo,
    submitButtonText,
    showSubmitButton = false,
    onSubmit,
    onGuestsChange,
  }: SelectGuestsFormProps) => {
    const { t } = useTranslation()

    const {
      adults,
      children,
      childrenAges,
      showChildrenBlock,
      handleChangeAdults,
      handleChangeChildren,
      handleChangeChildAge,
      handleAddChild,
      getGuestsState,
    } = useSelectGuests({
      initialState,
      onGuestsChange,
    })

    const guestsCount = useMemo(() => adults + children, [adults, children])

    const hasServerMaxGuests = maxGuests !== undefined

    const maxAdults = useMemo(
      () =>
        hasServerMaxGuests
          ? Math.max(0, maxGuests - children)
          : GuestsLimits.adults.max,
      [hasServerMaxGuests, maxGuests, children],
    )

    const maxChildren = useMemo(
      () =>
        hasServerMaxGuests
          ? Math.max(0, maxGuests - adults)
          : GuestsLimits.children.max,
      [hasServerMaxGuests, maxGuests, adults],
    )

    const adjustGuestsToMaxLimit = useCallback(() => {
      if (!hasServerMaxGuests) return

      const totalGuests = adults + children

      if (totalGuests > maxGuests) {
        const newAdults = Math.min(adults, maxGuests)
        const newChildren = Math.max(0, maxGuests - newAdults)

        if (newAdults !== adults) {
          handleChangeAdults(newAdults)
        }
        if (newChildren !== children) {
          handleChangeChildren(newChildren)
        }
      } else if (adults > maxAdults) {
        handleChangeAdults(maxAdults)
      }
    }, [
      maxGuests,
      hasServerMaxGuests,
      adults,
      children,
      maxAdults,
      handleChangeAdults,
      handleChangeChildren,
    ])

    useEffect(() => {
      adjustGuestsToMaxLimit()
    }, [maxGuests, adjustGuestsToMaxLimit])

    const handleSubmit = () => {
      const guestsState = getGuestsState()
      if (guestsState.adults > 0) {
        onSubmit?.(guestsState)
      }
    }

    const isSubmitDisabled = adults === 0

    const roomsCount = calculateRoomsCount(
      guestsCount,
      capacity?.max_guests_per_room || 0,
    )

    return (
      <>
        <div className={s.countPassenger}>
          <Paper padding={[12, 16]}>
            <ButtonInputValue
              title={t('kyrgyzstan_block.select_guests.adults')}
              subTitle={t('kyrgyzstan_block.select_guests.adults_description')}
              value={adults}
              minNumber={GuestsLimits.adults.min}
              maxNumber={maxAdults}
              onChange={handleChangeAdults}
            />
          </Paper>

          {isShowInfo && (
            <div className={clsx(s.maxGuestsInfo, 'medium-12')}>
              {t('kyrgyzstan_block.select_guests.max_guests_info', {
                count: maxGuests,
              })}
            </div>
          )}

          {!showChildrenBlock && (
            <Button
              type="button"
              variant="transparent"
              className={s.addChildButton}
              onClick={handleAddChild}
              disabled={
                capacity?.max_guests ? adults >= capacity.max_guests : false
              }
            >
              <AppIcon
                id={AllIcons.PLUS}
                height={24}
                width={24}
                className={s.addChildButtonIcon}
              />
              {t('kyrgyzstan_block.select_guests.add_child')}
            </Button>
          )}

          {showChildrenBlock && (
            <Paper padding={[12, 16]}>
              <ButtonInputValue
                title={t('kyrgyzstan_block.select_guests.children')}
                subTitle={t(
                  'kyrgyzstan_block.select_guests.children_description',
                )}
                value={children}
                minNumber={GuestsLimits.children.min}
                maxNumber={maxChildren}
                onChange={handleChangeChildren}
              />

              {!!childrenAges?.length && (
                <div className={s.additionalInfo}>
                  <div className={s.wrapperAges}>
                    {childrenAges?.map((age, index) => (
                      <ButtonInputValue
                        key={index}
                        variant="square"
                        value={age}
                        minNumber={GuestsLimits.childAge.min}
                        maxNumber={GuestsLimits.childAge.max}
                        title={t('kyrgyzstan_block.select_guests.child_age', {
                          count: index + 1,
                        })}
                        onChange={(value) => handleChangeChildAge(index, value)}
                      />
                    ))}
                  </div>

                  <div className={s.info}>
                    {t('kyrgyzstan_block.select_guests.child_age_info')}
                  </div>
                </div>
              )}
            </Paper>
          )}

          {isShowInfo && roomsCount > 0 && (
            <RoomsInfoMessage roomsCount={roomsCount} />
          )}
        </div>

        {showSubmitButton && (
          <ButtonContinue
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
          >
            <span className="bold-16">{submitButtonText || t('save')}</span>
          </ButtonContinue>
        )}
      </>
    )
  },
)
